import { cleanFloat, truncateTo } from "@/lib/tool-kit/grades/rounding";

/**
 * Foreign grade → German grade with the modified Bavarian formula.
 *
 *     X = 1 + 3 × (Nmax − Nd) ÷ (Nmax − Nmin)
 *
 * Nmax is the best grade on the home scale, Nmin the lowest pass, Nd the grade achieved.
 * Source: Kultusministerkonferenz, "Vereinbarung über die Festsetzung der Gesamtnote bei
 * ausländischen Hochschulzugangszeugnissen" (decision of 15 March 1991 as amended
 * 12 September 2013), section 3(1) and annex: the grade is determined to one decimal
 * place and not rounded ("es wird nicht gerundet").
 * https://www.kmk.org/fileadmin/pdf/ZAB/Hochschulzugang_Beschluesse_der_KMK/GesNot05.pdf
 *
 * The verbal bands for an averaged grade — up to 1.5 sehr gut, 1.6–2.5 gut, 2.6–3.5
 * befriedigend, 3.6–4.0 ausreichend, above 4.0 nicht ausreichend — are the ones German
 * examination regulations use (e.g. Heidelberg University, Institute of Psychology,
 * "Noten").
 *
 * The formula works for scales where a smaller number is better (Nmax 1, Nmin 4) as well
 * as larger-is-better ones, because only the ratio of differences matters.
 */

export interface GermanGradeResult {
  /** The formula's value before truncation, clamped at 1.0 from below. */
  exact: number;
  /** Cut to one decimal place, per the KMK rule. */
  grade: number;
  /** True when the result is worse than 4.0 — below the home scale's pass mark. */
  failing: boolean;
  /** True when Nd beat Nmax and the result was capped at 1.0. */
  capped: boolean;
}

export function toGermanGrade(
  nd: number,
  nmax: number,
  nmin: number
): GermanGradeResult | null {
  if (cleanFloat(nmax - nmin) === 0) {
    return null;
  }
  const raw = cleanFloat(1 + (3 * (nmax - nd)) / (nmax - nmin));
  const capped = raw < 1;
  const exact = capped ? 1 : raw;
  const grade = truncateTo(exact, 1);
  return { exact, grade, failing: grade > 4, capped };
}

/** German grade → the home-scale grade that converts to it (the formula solved for Nd). */
export function fromGermanGrade(
  grade: number,
  nmax: number,
  nmin: number
): number {
  return cleanFloat(nmax - ((grade - 1) * (nmax - nmin)) / 3);
}

export type GermanBand =
  | "sehr-gut"
  | "gut"
  | "befriedigend"
  | "ausreichend"
  | "nicht-ausreichend";

export function germanBand(grade: number): GermanBand {
  if (grade <= 1.5) {
    return "sehr-gut";
  }
  if (grade <= 2.5) {
    return "gut";
  }
  if (grade <= 3.5) {
    return "befriedigend";
  }
  if (grade <= 4) {
    return "ausreichend";
  }
  return "nicht-ausreichend";
}

export type GermanPreset =
  | "us-gpa"
  | "percent-50"
  | "percent-40"
  | "uk-percent"
  | "india-cgpa"
  | "france-20"
  | "netherlands-10";

/**
 * Starting values only: Nmax and Nmin come from the applicant's own institution, and
 * universities set them differently. US, UK, French and Dutch values are the ones the
 * TUM School of Computation, Information and Technology publishes for credit
 * recognition ("Conversion of Credits and Grades for Recognition"); the Indian value is
 * the UGC Choice Based Credit System pass grade (P = 4 on the 10-point scale).
 */
export const GERMAN_PRESETS: Record<
  GermanPreset,
  { nmax: number; nmin: number }
> = {
  "us-gpa": { nmax: 4, nmin: 1 },
  "percent-50": { nmax: 100, nmin: 50 },
  "percent-40": { nmax: 100, nmin: 40 },
  "uk-percent": { nmax: 85, nmin: 40 },
  "india-cgpa": { nmax: 10, nmin: 4 },
  "france-20": { nmax: 18, nmin: 10 },
  "netherlands-10": { nmax: 9, nmin: 6 },
};
